import { useState, useCallback } from 'react';
import useRecipeV2Api from './useRecipeV2Api';
import { RecipeV2Detail } from '../types';
import { demoRecipeV2Client } from '../../../app/services/api/recipeV2';
import { isDemoModeEnabled } from '../../../utils/featureAccess';
import isDemoNetworkFailure from './isDemoNetworkFailure';

const useRecipeV2Detail = () => {
  const api = useRecipeV2Api();
  const [recipe, setRecipe] = useState<RecipeV2Detail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDemoSnapshot, setIsDemoSnapshot] = useState(false);

  const loadRecipe = useCallback(async (id: number) => {
    setIsLoading(true);
    setError(null);
    setIsDemoSnapshot(false);
    try {
      let detail: RecipeV2Detail;
      try {
        detail = await api.getRecipeDetail(id);
      } catch (cause) {
        // A disconnected trade-show demo can display its local fixture. An HTTP error
        // (including 401/403) must keep the governed API's normal error behaviour.
        if (!isDemoNetworkFailure(cause) || !isDemoModeEnabled()) throw cause;
        detail = await demoRecipeV2Client.getRecipeDetail(id);
        setIsDemoSnapshot(true);
      }
      const pricedComponents = (detail.components ?? []).filter(component => component.costPrice != null);
      const derivedCost = pricedComponents.length > 0
        ? pricedComponents.reduce((sum, component) => sum + component.costPrice! * component.quantity, 0)
        : undefined;
      const totalCost = detail.totalCost ?? derivedCost;
      const primaryYield = [...(detail.yields ?? [])].sort((left, right) => left.displayOrder - right.displayOrder)[0];
      setRecipe({
        ...detail,
        totalCost,
        costPerPortion: detail.costPerPortion ?? (totalCost != null && primaryYield?.quantity > 0
          ? totalCost / primaryYield.quantity
          : undefined),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipe');
      setRecipe(null);
      setIsDemoSnapshot(false);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  return { recipe, isLoading, error, isDemoSnapshot, loadRecipe, setRecipe };
};

export default useRecipeV2Detail;
